import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/lib/hooks'
import { fetchPeopleOverview, fetchPersonDetail, clearPersonDetail, clearErrors } from './jiraSlice'

export function useJiraPeopleOverview() {
  const dispatch = useAppDispatch()
  const {
    peopleOverview,
    isLoadingOverview,
    overviewError,
    lastFetchTime,
  } = useAppSelector((state) => state.jira)

  const fetch = useCallback(() => {
    dispatch(fetchPeopleOverview())
  }, [dispatch])

  const refresh = useCallback(() => {
    dispatch(fetchPeopleOverview())
  }, [dispatch])

  useEffect(() => {
    if (!peopleOverview && !isLoadingOverview && !overviewError) {
      fetch()
    }
  }, [peopleOverview, isLoadingOverview, overviewError, fetch])

  return {
    data: peopleOverview,
    isLoading: isLoadingOverview,
    error: overviewError,
    lastFetchTime,
    refresh,
  }
}

export function useJiraPersonDetail(accountId: string | null) {
  const dispatch = useAppDispatch()
  const {
    selectedPersonDetail,
    isLoadingDetail,
    detailError,
  } = useAppSelector((state) => state.jira)

  const fetch = useCallback(() => {
    if (accountId) {
      dispatch(fetchPersonDetail(accountId))
    }
  }, [dispatch, accountId])

  const clear = useCallback(() => {
    dispatch(clearPersonDetail())
  }, [dispatch])

  useEffect(() => {
    if (accountId) {
      fetch()
    }
    return () => {
      clear()
    }
  }, [accountId, fetch, clear])

  return {
    data: selectedPersonDetail,
    isLoading: isLoadingDetail,
    error: detailError,
    refresh: fetch,
    clear,
  }
}

export function useJiraErrors() {
  const dispatch = useAppDispatch()
  const { overviewError, detailError } = useAppSelector((state) => state.jira)

  const clear = useCallback(() => {
    dispatch(clearErrors())
  }, [dispatch])

  return {
    overviewError,
    detailError,
    hasError: Boolean(overviewError || detailError),
    clearErrors: clear,
  }
}

